// Store configuration utility
// Manages store item configuration with Firestore persistence and localStorage fallback

import {
  getStoreConfigFromFirestore,
  saveStoreConfigToFirestore,
  DEFAULT_STORE_CONFIG,
  migrateLocalStorageToFirestore,
} from "lib/purchaseUtils"

const STORAGE_KEY = "choreStoreConfig"

// Cache for store config to avoid excessive Firestore calls
let storeConfigCache = null
let cacheTimestamp = null
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

// Get current store configuration (async version for Firestore)
export const getStoreConfigAsync = async (forceRefresh = false) => {
  try {
    // Check cache first (unless force refresh)
    if (
      !forceRefresh &&
      storeConfigCache &&
      cacheTimestamp &&
      Date.now() - cacheTimestamp < CACHE_DURATION
    ) {
      return storeConfigCache
    }

    // Try to get from Firestore
    const firestoreConfig = await getStoreConfigFromFirestore()


    // Update cache
    storeConfigCache = firestoreConfig
    cacheTimestamp = Date.now()

    return firestoreConfig
  } catch (error) {

    // Fallback to localStorage
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        return validateStoreConfig(parsed) ? parsed : DEFAULT_STORE_CONFIG
      }
    } catch (localError) {
    }

    return DEFAULT_STORE_CONFIG
  }
}

// Synchronous version for backward compatibility (will use cache or default)
export const getStoreConfig = () => {
  // If we have cached data, return it
  if (
    storeConfigCache &&
    cacheTimestamp &&
    Date.now() - cacheTimestamp < CACHE_DURATION
  ) {
    return storeConfigCache
  }

  // Otherwise, try localStorage as fallback
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return validateStoreConfig(parsed) ? parsed : DEFAULT_STORE_CONFIG
    }
  } catch (error) {
  }

  return DEFAULT_STORE_CONFIG
}

// Save store configuration (async version for Firestore)
export const saveStoreConfigAsync = async (config) => {
  try {
    if (!validateStoreConfig(config)) {
      throw new Error("Invalid store configuration")
    }

    // Save to Firestore
    await saveStoreConfigToFirestore(config)

    // Update cache
    storeConfigCache = config
    cacheTimestamp = Date.now()

    // Also save to localStorage as backup
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config))

    return true
  } catch (error) {

    // Try localStorage fallback
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config))
      return true
    } catch (localError) {
      return false
    }
  }
}

// Synchronous version for backward compatibility (localStorage only)
export const saveStoreConfig = (config) => {
  try {
    if (!validateStoreConfig(config)) {
      throw new Error("Invalid store configuration")
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config))

    // Also update cache
    storeConfigCache = config
    cacheTimestamp = Date.now()

    return true
  } catch (error) {
    return false
  }
}

// Migrate existing localStorage data to Firestore
export const migrateToFirestore = async () => {
  try {
    const migratedConfig = await migrateLocalStorageToFirestore()

    // Update cache
    storeConfigCache = migratedConfig
    cacheTimestamp = Date.now()

    return migratedConfig
  } catch (error) {
    return DEFAULT_STORE_CONFIG
  }
}

// Validate store configuration structure
export const validateStoreConfig = (config) => {
  if (!Array.isArray(config)) {
    return false
  }

  for (let i = 0; i < config.length; i++) {
    const item = config[i]

    // Check required fields
    if (typeof item.id !== "number" || item.id <= 0) {
      return false
    }

    if (typeof item.name !== "string" || item.name.trim() === "") {
      return false
    }

    if (typeof item.description !== "string") {
      return false
    }

    if (typeof item.price !== "number" || item.price < 0) {
      return false
    }

    if (typeof item.category !== "string" || item.category.trim() === "") {
      return false
    }

    if (typeof item.inStock !== "boolean") {
      return false
    }

    if (typeof item.featured !== "boolean") {
      return false
    }
  }

  return true
}

// Reset store configuration to default (async)
export const resetStoreConfigAsync = async () => {
  return await saveStoreConfigAsync(DEFAULT_STORE_CONFIG)
}

// Reset store configuration to default (sync - localStorage only)
export const resetStoreConfig = () => {
  return saveStoreConfig(DEFAULT_STORE_CONFIG)
}

// Update a specific store item (async)
export const updateStoreItemAsync = async (itemId, updates) => {
  const currentConfig = await getStoreConfigAsync()
  const itemIndex = currentConfig.findIndex((item) => item.id === itemId)

  if (itemIndex === -1) {
    return false
  }

  const updatedConfig = [...currentConfig]
  updatedConfig[itemIndex] = { ...updatedConfig[itemIndex], ...updates }

  return await saveStoreConfigAsync(updatedConfig)
}

// Update a specific store item (sync)
export const updateStoreItem = (itemId, updates) => {
  const currentConfig = getStoreConfig()
  const itemIndex = currentConfig.findIndex((item) => item.id === itemId)

  if (itemIndex === -1) {
    return false
  }

  const updatedConfig = [...currentConfig]
  updatedConfig[itemIndex] = { ...updatedConfig[itemIndex], ...updates }

  return saveStoreConfig(updatedConfig)
}

// Add a new store item (async)
export const addStoreItemAsync = async (newItem) => {
  const currentConfig = await getStoreConfigAsync()
  const maxId = Math.max(...currentConfig.map((item) => item.id), 0)

  const itemWithId = {
    ...newItem,
    id: maxId + 1,
    inStock: newItem.inStock ?? true,
    featured: newItem.featured ?? false,
  }

  const success = await saveStoreConfigAsync([...currentConfig, itemWithId])
  return success ? itemWithId : null
}

// Add a new store item (sync)
export const addStoreItem = (newItem) => {
  const currentConfig = getStoreConfig()
  const maxId = Math.max(...currentConfig.map((item) => item.id), 0)

  const itemWithId = {
    ...newItem,
    id: maxId + 1,
    inStock: newItem.inStock ?? true,
    featured: newItem.featured ?? false,
  }

  const updatedConfig = [...currentConfig, itemWithId]
  return saveStoreConfig(updatedConfig) ? itemWithId : null
}

// Remove a store item (async)
export const removeStoreItemAsync = async (itemId) => {
  const currentConfig = await getStoreConfigAsync()
  const updatedConfig = currentConfig.filter((item) => item.id !== itemId)

  if (updatedConfig.length === currentConfig.length) {
    return false
  }

  return await saveStoreConfigAsync(updatedConfig)
}

// Remove a store item (sync)
export const removeStoreItem = (itemId) => {
  const currentConfig = getStoreConfig()
  const updatedConfig = currentConfig.filter((item) => item.id !== itemId)

  if (updatedConfig.length === currentConfig.length) {
    return false
  }

  return saveStoreConfig(updatedConfig)
}

// Clear cache (useful for forcing refresh)
export const clearStoreConfigCache = () => {
  storeConfigCache = null
  cacheTimestamp = null
}

// Export DEFAULT_STORE_CONFIG for backward compatibility
export { DEFAULT_STORE_CONFIG }
